// Persistencia del cliente seleccionado entre pantallas (por módulo operativo).

import { useCallback, useEffect, useState } from "react";
import {
  CLIENT_MODULE_SCOPES,
  migrateLegacyClientId,
  storageKeyForScope,
  useClientModuleScope,
} from "@/lib/clientModuleScope";

const ALL_CLIENT_SCOPES = Object.values(CLIENT_MODULE_SCOPES);

function dispatchClientChanged(clientId, scope) {
  window.dispatchEvent(
    new CustomEvent("visor:client-changed", {
      detail: { clientId, scope },
    }),
  );
}

/** Limpia la selección de cliente en todos los módulos (p. ej. al cerrar sesión). */
export function clearAllStoredClientIds() {
  try {
    migrateLegacyClientId(CLIENT_MODULE_SCOPES.VISOR_SAT);
    localStorage.removeItem("vf:selected_client_id");
    ALL_CLIENT_SCOPES.forEach((scope) => {
      localStorage.removeItem(storageKeyForScope(scope));
    });
    ALL_CLIENT_SCOPES.forEach((scope) => dispatchClientChanged("", scope));
  } catch {
    /* ignore */
  }
}

/** @deprecated Use getStoredClientId(scope) — lectura legacy sin ámbito. */
export const getStoredClientId = (scope) => {
  try {
    const resolvedScope = scope || CLIENT_MODULE_SCOPES.VISOR_SAT;
    migrateLegacyClientId(resolvedScope);
    const scoped = localStorage.getItem(storageKeyForScope(resolvedScope)) || "";
    if (scoped) return scoped;
    if (resolvedScope === CLIENT_MODULE_SCOPES.VISOR_SAT) {
      return localStorage.getItem("vf:selected_client_id") || "";
    }
    for (const otherScope of ALL_CLIENT_SCOPES) {
      if (otherScope === resolvedScope) continue;
      const shared = localStorage.getItem(storageKeyForScope(otherScope)) || "";
      if (shared) return shared;
    }
    return "";
  } catch {
    return "";
  }
};

/**
 * Persiste el cliente de trabajo en todos los módulos operativos de la sesión.
 * Así el mismo RFC se conserva al cambiar entre Visor, Laboral y Trámites.
 */
export const setStoredClientId = (id, scope) => {
  try {
    const next = id ? String(id) : "";
    let changed = false;
    ALL_CLIENT_SCOPES.forEach((moduleScope) => {
      const key = storageKeyForScope(moduleScope);
      const prev = localStorage.getItem(key) || "";
      if (next === prev) return;
      changed = true;
      if (next) localStorage.setItem(key, next);
      else localStorage.removeItem(key);
    });
    const legacyKey = "vf:selected_client_id";
    const legacyPrev = localStorage.getItem(legacyKey) || "";
    if (next !== legacyPrev) {
      changed = true;
      if (next) localStorage.setItem(legacyKey, next);
      else localStorage.removeItem(legacyKey);
    }
    if (!changed) return;
    ALL_CLIENT_SCOPES.forEach((moduleScope) => dispatchClientChanged(next, moduleScope));
  } catch {
    /* ignore */
  }
};

/**
 * Sincroniza el cliente de trabajo global cuando el despacho ya eligió empresa
 * (seguimiento, tareas, información fiscal). Mantiene la barra superior alineada.
 */
export function syncWorkClientFromDespacho(clientId) {
  const next = clientId ? String(clientId).trim() : "";
  if (!next) return;
  setStoredClientId(next);
}

/**
 * Hook [clientId, setClientId] sincronizado con localStorage del ámbito del módulo.
 * @param {Array} clients lista actual de clientes
 * @param {object} [options]
 * @param {string} [options.moduleScope] Ámbito explícito; si no, usa ClientModuleScopeProvider.
 * @param {boolean} [options.autoPickFirst=false] Solo elige el primero si no hay id guardado.
 * @param {string} [options.preferRfc]
 */
export function usePersistedClientId(clients, options = {}) {
  const contextScope = useClientModuleScope();
  const moduleScope = options.moduleScope || contextScope || CLIENT_MODULE_SCOPES.VISOR_SAT;
  const autoPickFirst = options.autoPickFirst === true;
  const preferRfc = (options.preferRfc || "").trim().toUpperCase();
  const storageKey = storageKeyForScope(moduleScope);

  const readStored = () => {
    migrateLegacyClientId(moduleScope);
    try {
      return localStorage.getItem(storageKey) || "";
    } catch {
      return "";
    }
  };

  const [clientId, setClientIdState] = useState(readStored);

  useEffect(() => {
    if (!clients?.length) return;
    if (preferRfc) {
      const match = clients.find((c) => (c.rfc || "").trim().toUpperCase() === preferRfc);
      if (match) {
        setClientIdState(match.id);
        setStoredClientId(match.id, moduleScope);
        return;
      }
    }
    const stored = readStored();
    if (stored && clients.some((c) => c.id === stored)) {
      setClientIdState((prev) => (prev !== stored ? stored : prev));
    } else if (autoPickFirst) {
      setClientIdState(clients[0].id);
      setStoredClientId(clients[0].id, moduleScope);
    } else if (stored && !clients.some((c) => c.id === stored)) {
      setClientIdState("");
      setStoredClientId("", moduleScope);
    }
  }, [clients, autoPickFirst, preferRfc, moduleScope, storageKey]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === storageKey) {
        setClientIdState(e.newValue || "");
      }
    };
    const onClientChanged = (e) => {
      const evtScope = e?.detail?.scope;
      if (evtScope && evtScope !== moduleScope) return;
      setClientIdState(e?.detail?.clientId ?? readStored());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("visor:client-changed", onClientChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("visor:client-changed", onClientChanged);
    };
  }, [moduleScope, storageKey]);

  const setClientId = useCallback(
    (id) => {
      const next = id ? String(id) : "";
      setClientIdState((prev) => (prev === next ? prev : next));
      setStoredClientId(next, moduleScope);
    },
    [moduleScope],
  );

  return [clientId, setClientId];
}
